/**
 * The guest wire contract, transcribed from the SERVER, not from memory.
 *
 * Sources (autoryn backend, HEAD 84f33926):
 *   src/routes/guestExternalCollaboration.ts   — the /guest mount: 36 endpoints, the EID slice is below
 *   src/services/externalCollaboration/guestAccessService.ts       — guestProjectIssue / guestProjectIssueDesk
 *   src/services/externalCollaboration/externalIssueService.ts     — desks, submit, list, approve, reopen
 *   src/services/externalCollaboration/externalIssueThreadService.ts — comments, info-response
 *
 * Two properties of this contract are load-bearing and easy to get wrong; both are encoded in the types.
 *
 * 1. THE CAPABILITY KEYS ARE OPTIONAL — and their ABSENCE is not `false`.
 *    The server emits canApproveResolution / canRequestReopen / canComment only where it already had the
 *    desk in hand (single read, submit response, action responses). On the LIST path there is no desk, so
 *    the keys are ABSENT. Rendering "absent" as "not allowed" would hide those controls on every issue
 *    opened from a list — i.e. all of them — a total feature loss nothing would report as an error.
 *    Hence `?: boolean` (not `boolean`) and the `allowed()` helper that demands an explicit true.
 *
 * 2. `resolutionSummary` IS GONE (founder A3, backend 84f33926). It was always `undefined`; the customer-
 *    facing resolution text is `publicStatusNote`. Do not add it back to this type.
 */

/** Every guest refusal. `code` is machine-readable and stable; `message` is already human — the server maps it. */
export interface GuestError {
  type: 'error'
  code: string
  message: string
  retryAfterSec?: number
}

export type PublicStatus =
  | 'received' | 'in_review' | 'in_progress' | 'waiting_on_you' | 'resolved' | 'declined' | 'closed'

/** GET /guest/invitations/:token — PRE-session, so it says as little as possible. */
export interface InvitationView {
  invitationNumber: string
  invitationType: string
  /** A four-value PUBLIC status. The raw GuestInvitation lifecycle enum is deliberately NOT exposed. */
  status: 'pending' | 'accepted' | 'expired' | 'unavailable'
  expiresAt?: string
  acceptable: boolean
}

/** POST /guest/invitations/:token/accept — the token is returned EXACTLY ONCE and there is no refresh. */
export interface AcceptedSession {
  sessionToken: string
  workspaceId?: string
  expiresAt?: string
}

/** GET /guest/issue-desks, /guest/issue-desks/:deskId */
export interface Desk {
  deskId: string
  deskNumber?: string
  name: string
  description?: string
  issueTypes: string[]
  severities: string[]
  requireEnvironment?: boolean
  allowGuestComments?: boolean
}

/** GET /guest/issues, /guest/issues/:issueId, and every issue-scoped action response. */
export interface Issue {
  issueId: string
  reference: string
  title: string
  summary?: string
  issueType?: string
  severityReported?: string
  publicStatus: PublicStatus
  publicStatusNote?: string
  infoRequestOpen: boolean
  infoRequestPrompt?: string
  reopenRequestStatus?: string | null
  /**
   * When THIS customer confirmed the resolution — the durable fact behind the confirmation, added server-side
   * 2026-07-30 after the live walk found the screen thanked them and then asked again on the next load.
   * Present on the list path too (it is a fact, not a per-desk capability). `null` = not confirmed.
   */
  resolutionApprovedAt?: string | null
  threadReference?: string
  submittedAt?: string
  lastUpdateAt?: string
  // ⚠️ OPTIONAL BY DESIGN — absent on the list path (no desk). See the header note.
  canApproveResolution?: boolean
  canRequestReopen?: boolean
  canComment?: boolean
}

/**
 * A comment as the SERVER actually projects it (`guestProjectComment` in guestAccessService.ts):
 *   { commentNumber, authorType, body, status, createdAt }
 *
 * 🔴 THIS TYPE WAS WRONG IN EVERY FIELD THAT MATTERED, and being wrong cost nothing at compile time because
 * nothing validates a response against it. It declared `commentId` (server sends `commentNumber`),
 * `authorKind` (server sends `authorType`) and the value `'staff'` (the model enum is
 * `['guest','internal_user']` — 'staff' does not exist anywhere). The screen tested
 * `authorKind === 'guest'`, which was `undefined === 'guest'` for every row, so the portal attributed the
 * CUSTOMER'S OWN messages to "Our team" — on a support thread, where who said what is the entire point.
 *
 * There is deliberately NO `authorName`: the server does not disclose staff names to a guest. Do not add one
 * back hoping it appears; render the side, not the person.
 */
export interface IssueComment {
  commentNumber: string
  body: string
  authorType: 'guest' | 'internal_user'
  status?: string
  createdAt: string
}

/**
 * A capability is granted ONLY by an explicit `true`. `undefined` means "the server did not say here" —
 * which is the list path — and must render as "we don't know", never as "you may not".
 */
export const allowed = (cap: boolean | undefined): boolean => cap === true

/** True when the server has told us about this capability at all (i.e. it had the desk). */
export const capabilityKnown = (cap: boolean | undefined): boolean => cap !== undefined

/**
 * 🔴 THE SESSION-DEATH PATH WAS UNREACHABLE, and it is the ONE failure every guest is guaranteed to hit.
 *
 * The recovery branch matched three lowercase codes (`no_session`, `no_guest`, `guest_inactive`). None of them
 * is emitted by the guest middleware, which speaks SCREAMING_SNAKE — the real set is below, read off
 * `src/middleware/guestAuth.ts`. Sessions expire after 168h (DEFAULT_SESSION_EXPIRY_HOURS, set on every
 * accept), so at seven days EVERY guest returns to a token still sitting in localStorage: `hasSession()` is
 * true, the screen mounts, the read 401s with `GUEST_SESSION_EXPIRED`, no branch matches, and they get a
 * generic "something went wrong on our side" with a dead token that is never cleared — instead of being sent
 * to the "open your invitation link again" screen. Retrying does the same thing forever.
 *
 * `no_session` is kept because the CLIENT itself raises it (client.ts, before any request, when no token is
 * held). The rest are the server's.
 */
const SESSION_DEAD_CODES = [
  'no_session',                 // raised locally by client.ts — no token to send
  'GUEST_NO_TOKEN',
  'GUEST_INVALID_SESSION',
  'GUEST_SESSION_INACTIVE',
  'GUEST_SESSION_REVOKED',
  'GUEST_SESSION_EXPIRED',
  'GUEST_SESSION_NO_TENANT',
  'GUEST_AUTH_ERROR',
] as const

export const isSessionDead = (code: string): boolean =>
  (SESSION_DEAD_CODES as readonly string[]).indexOf(code) !== -1

/**
 * Whether the guest may CONFIRM a resolution.
 *
 * Gate on the STATUS, never on the presence of resolution text: the field that used to carry that text had
 * no writer, so gating on it hid the controls forever and presented as "customers never confirm resolutions".
 *
 * `resolved` ONLY, and that is correct rather than narrow: `recordResolutionApproval` filters
 * `effectivePublicStatus: 'resolved'` exactly and answers 409 `issue_not_resolved` otherwise, so offering the
 * button on a closed issue would offer an action the server refuses.
 */
export const isResolutionDecidable = (issue: Pick<Issue, 'publicStatus'>): boolean =>
  issue.publicStatus === 'resolved'

/**
 * Whether the guest may ASK FOR A REOPEN — a WIDER set than approval, which is the whole point of separating
 * them. `requestIssueReopen` accepts `resolved` OR `closed` (externalIssueProjectionService), but the portal
 * gated the entire resolution section on `isResolutionDecidable`, so a CLOSED issue rendered no section at all
 * and "Ask to reopen" was unreachable — even on a desk with `allowGuestReopenRequests` on and the server
 * emitting `canRequestReopen: true` for it. The customer's only route back is to file a duplicate issue.
 */
export const isReopenable = (issue: Pick<Issue, 'publicStatus'>): boolean =>
  issue.publicStatus === 'resolved' || issue.publicStatus === 'closed'


/* ══ WORKSPACE SURFACES ═════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 THESE EXISTED ON THE SERVER THE WHOLE TIME AND NO CLIENT COULD REACH THEM.
 *
 * Every guest item route validates its parameter with `isOid`, and until 2026-08-09 no guest response
 * carried an id — `issueId` was the only address the API ever emitted. That, not a product decision, is why
 * this portal was a support desk and nothing else: files, approvals, forms and quotes were built, guarded
 * and unreachable. The backend now emits `workspaceId`, `sharedItemId`, `filePackageId`, `approvalId`,
 * `formId`, `uploadRequestId` and `clientUpdateId`, and four workspace-scoped lists were added beside the
 * two that existed.
 *
 * ⚠️ TRANSCRIBED FROM THE SERVER PROJECTIONS (`guestAccessService.ts`), not from the models and not from
 * the host-side API, which returns different shapes. The masked variants are what a guest gets:
 * `summaryMasked` arrives as `summary`, `instructionsMasked` as `instructions`.
 */

/** GET /guest/workspaces — envelope `{items, count}`. */
export interface Workspace {
  workspaceId: string
  workspaceNumber: string
  name: string
  description?: string
  workspaceType?: string
  status?: string
  visibility?: string
  expiresAt?: string
}

/** GET /guest/workspaces/:id/shared-items — an RFQ is one of these, distinguished by its grant, not its type. */
export interface SharedItem {
  sharedItemId: string
  sharedItemNumber: string
  title: string
  summary?: string
  /** Present only when `externalVisibility` is a content-visible mode; absent is NOT empty. */
  content?: string
  externalVisibility?: string
  allowedFields?: string[]
  limitations?: string[]
}

/** GET /guest/workspaces/:id/file-packages */
export interface FilePackage {
  filePackageId: string
  packageNumber: string
  title: string
  description?: string
  packageType?: string
  status?: string
  fileCount: number
  /** e.g. ['download']. A package may be viewable and not downloadable — do not assume. */
  allowedActions?: string[]
  watermarkEnabled?: boolean
  expiresAt?: string
}

/**
 * POST /guest/file-packages/:id/record-download
 *
 * ⚠️ THE URL IS ONE-TIME AND SHORT-LIVED, and the server may legitimately answer with none — the two
 * cases are DIFFERENT and the screen must not render them the same way. `downloadAvailable: false` means
 * the package has no servable file; it is not an error and it is not a link.
 */
export interface DownloadTicket {
  recorded: boolean
  downloadUrlOneTime?: string
  downloadAvailable?: false
}

/** GET /guest/workspaces/:id/approvals */
export interface ApprovalRequest {
  approvalId: string
  externalApprovalNumber: string
  approvalType?: string
  title: string
  description?: string
  status?: string
  responseRequiredBy?: string
  /**
   * 🔴 THE ONLY RESPONSES THE SERVER WILL ACCEPT. `guestRespondApproval` refuses anything outside this
   * list with `response_not_allowed`, so the screen must render BUTTONS FROM THIS ARRAY rather than a fixed
   * approve/reject pair — offering an action the server refuses is the defect this portal keeps finding.
   */
  allowedResponses?: string[]
}

export interface FormField {
  fieldId: string
  label: string
  fieldType?: string
  required?: boolean
  options?: string[]
}

/** GET /guest/workspaces/:id/forms */
export interface FormTemplate {
  formId: string
  externalFormNumber: string
  name: string
  description?: string
  formType?: string
  status?: string
  fields: FormField[]
  expiresAt?: string
}

/** GET /guest/workspaces/:id/client-updates */
export interface ClientUpdate {
  clientUpdateId: string
  shareNumber: string
  title: string
  summary?: string
  status?: string
  viewedAt?: string
  acknowledgedAt?: string
  expiresAt?: string
}

/**
 * A write acknowledgement. The server projects every guest write through an ALLOW-LIST (`RECEIPT_KEYS`), so
 * a receipt carries a reference and a state and nothing else — never the row it wrote.
 */
export interface WriteReceipt {
  ok?: boolean
  status?: string
  reference?: string
  responseNumber?: string
  submissionNumber?: string
  supplierQuoteSubmissionNumber?: string
  submittedAt?: string
  respondedAt?: string
}